import {
  useCompassMenu,
  useLanguage,
  useMeaningValueTestResult,
  useNeedsProTestResult,
  useNeedsTestResult,
  usePreferencePersonalityTestResult,
} from '@orientaction/hooks';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FC } from 'react';
import { dataEmpty as personalityDataEmpty } from '../../../GlobalRestitution/PersonalityCard/data';
import Score from '../../../GlobalRestitution/PersonalityCard/Score';
import { dataEmpty as talentsDataEmpty } from '../../../GlobalRestitution/TalentsCard/data';
import RedesignImage from '../../shared/components/RedesignImage';
import HarmonyResultChart from '../../Tests/Major/Result/components/HarmonyResultChart';
import { needsAssets } from '../../Tests/Major/Result/content/needsPresentation';
import { preferenceRankColors } from '../../Tests/Major/Result/content/preferencePresentation';
import locale from '../locale.json';
import CompassResultSection from './CompassResultSection';
import { useCompassSectionsStyles } from './CompassSections.styles';
import CompassValuePodium from './CompassValuePodium';

Chart.register(ChartDataLabels);

interface CompassSectionsProps {
  harmonyTestLastResult: any;
  loading: boolean;
  canAccessNeeds: boolean;
  showNeeds: boolean;
  showNeedsPro: boolean;
}

const CompassSections: FC<CompassSectionsProps> = ({
  harmonyTestLastResult,
  loading,
  canAccessNeeds,
  showNeeds,
  showNeedsPro,
}) => {
  const classes = useCompassSectionsStyles();
  const { language } = useLanguage();
  const {
    meaningTestLastResult,
    needsTestLastResult,
    needsProTestLastResult,
    preferenceTestLastResult,
  } = useCompassMenu();
  const { getMyResult: getMeaningValueResult } = useMeaningValueTestResult();
  const { getMyResult: getNeedsResult } = useNeedsTestResult();
  const { getMyResult: getNeedsProResult } = useNeedsProTestResult();
  const { getMyResult: getPreferencePersonalityResult } = usePreferencePersonalityTestResult();

  return (
    <>
      <CompassResultSection
        id="meaning-value"
        title={locale.sectionValues}
        description={locale.valuesDescription}
        templateId={1}
        lastResultId={meaningTestLastResult?.id}
        getResult={getMeaningValueResult}
        selectResult={data => data.data.resultat}
        renderVisual={(result: any) => (
          <div className={classes.podiumWrap}>
            <CompassValuePodium results={result} />
          </div>
        )}
        to={language === 'GB' ? '/en/tests/meaning-values' : '/tests/meaning-value'}
        toResultat={`/resultat-meaning-value/${meaningTestLastResult?.id}`}
        buyRedirectUri="https://orientaction.kneo.me/shop/view/D0A37F"
      />

      {canAccessNeeds && showNeeds && (
        <>
          <CompassResultSection
            id="besoins"
            title={locale.sectionNeedsPerso}
            description={locale.needsPersoDescription}
            templateId={2}
            lastResultId={needsTestLastResult?.id}
            getResult={getNeedsResult}
            selectResult={data => data?.data?.perso?.favorite}
            renderVisual={(result: any) => {
              const pyramidImageSource =
                needsAssets[result?.key]?.pyramid ||
                result?.imgPyramid?.replace(
                  /\/need\//g,
                  language === 'GB' ? '/need/pyramids/en/' : '/need/pyramids/'
                );

              return (
                <RedesignImage
                  src={pyramidImageSource || '/images/need/pyramid_neutre.png'}
                  className={classes.pyramidImage}
                  alt="Pyramide des besoins personnels"
                />
              );
            }}
            to={language === 'GB' ? '/en/tests/needs-pro' : '/tests/besoins-pro'}
            toResultat={`/resultat-need/${needsTestLastResult?.id}`}
            buyRedirectUri="https://orientaction.kneo.me/shop/view/120C39"
          />

          <CompassResultSection
            title={locale.sectionNeedsPro}
            description={locale.needsProDescription}
            templateId={2}
            lastResultId={needsTestLastResult?.id}
            getResult={getNeedsResult}
            selectResult={data => data?.data?.pro?.favorite}
            renderVisual={(result: any) => {
              const pyramidImageSource =
                needsAssets[result?.key]?.pyramid ||
                result?.imgPyramid?.replace(/\/need\//g, '/need/pyramids/');

              return (
                <RedesignImage
                  src={pyramidImageSource || '/images/need/pyramid_neutre.png'}
                  className={classes.pyramidImage}
                  alt="Pyramide des besoins professionnels"
                />
              );
            }}
            to={language === 'GB' ? '/en/tests/needs' : '/tests/besoins'}
            toResultat={`/resultat-need/${needsTestLastResult?.id}`}
            buyRedirectUri="https://orientaction.kneo.me/shop/view/120C39"
          />
        </>
      )}

      {showNeedsPro && (
        <CompassResultSection
          id="besoins-pro"
          title={locale.sectionNeedsPro}
          description={locale.needsProDescription}
          templateId={3}
          lastResultId={needsProTestLastResult?.id}
          getResult={getNeedsProResult}
          selectResult={data => data.data.favorite}
          renderVisual={(result: any) => {
            const pyramidImageSource =
              needsAssets[result?.key]?.pyramid ||
              result?.imgPyramid?.replace(
                /\/need\//g,
                language === 'GB' ? '/need/pyramids/en/' : '/need/pyramids/'
              );

            return (
              <RedesignImage
                src={pyramidImageSource || '/images/need/pyramid_neutre.png'}
                className={classes.pyramidImage}
                alt="Pyramide des besoins professionnels"
              />
            );
          }}
          to={language === 'GB' ? '/en/tests/needs-pro' : '/tests/besoins-pro'}
          toResultat={`/resultat-need-pro/${needsProTestLastResult?.id}`}
          buyRedirectUri="https://orientaction.kneo.me/shop/view/5726D7"
        />
      )}

      <CompassResultSection
        id="preference-personality"
        title={locale.sectionPersonality}
        description={locale.personalityDescription}
        templateId={4}
        lastResultId={preferenceTestLastResult?.id}
        getResult={getPreferencePersonalityResult}
        selectResult={data => data?.data?.resultat}
        renderVisual={(result: any[] | undefined) => {
          const displayData = result?.length ? result : personalityDataEmpty;

          return (
            <div className={classes.personalityChart}>
              {displayData.map((item: any, index: number) => (
                <div key={item.key || item.title}>
                  <div
                    className={classes.personalityTitle}
                    style={{ color: preferenceRankColors[index] }}>
                    {item.title}
                  </div>
                  <Score listScore={item.score} color={preferenceRankColors[index]} />
                </div>
              ))}
            </div>
          );
        }}
        to={
          language === 'GB' ? '/en/tests/preferences-personality' : '/tests/preference-personality'
        }
        toResultat={`/resultat-preference/${preferenceTestLastResult?.id}`}
        buyRedirectUri="https://orientaction.kneo.me/shop/view/752F26"
      />

      <CompassResultSection
        id="soft-skills"
        title={locale.sectionTalents}
        description={locale.talentsDescription}
        templateId={5}
        lastResultId={harmonyTestLastResult?.id}
        renderVisual={(_, showNeutralVersion) => (
          <div className={classes.talentsChart}>
            <HarmonyResultChart
              isEmpty={showNeutralVersion}
              result={showNeutralVersion ? talentsDataEmpty : harmonyTestLastResult}
              fromCompass={true}
            />
          </div>
        )}
        to={language === 'GB' ? '/en/tests/harmony' : '/tests/soft-skills'}
        toResultat={`/resultat-harmony/${harmonyTestLastResult?.id}`}
        buyRedirectUri="https://orientaction.kneo.me/shop/view/1755C3"
        externalLoading={loading}
      />
    </>
  );
};

export default CompassSections;
